import { useCallback, useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { addYears, isAfter, isSameDay } from 'date-fns'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { dateRangeOptionsKeys } from 'pages/Tasks/AbsenceRequest/constants'
import { ZERO } from 'shared/constants'
import { absenceRequestDateFormat } from 'utils/constants'
import { daysIncludedInRange, getFormattedFromDate, newDateWithoutTime } from 'utils/date'
import { DAY_PICKER_MAX_YEARS, NUM_DAYS_THRESHOLD } from '../constants'
import type { UseAbsenceDateSelection, OnChangeProps } from '../types'

export const useAbsenceDateSelection = ({
  formFields,
  setValue,
  selectedPersonTenant,
  firstAvailableDate,
  formErrors,
}: UseAbsenceDateSelection) => {
  const { t, locale } = useFavurTranslation()
  const { palette, breakpoints } = useTheme()
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'))
  const {
    toDate: { value: toDate },
    fromDate: { value: fromDate },
    dateRange: { value: dateRange },
  } = formFields
  const { fromDate: fromDateError, toDate: toDateError } = formErrors
  const today = newDateWithoutTime()
  const handleAcceptFromDate = useCallback(
    (newValue: Date | null) => {
      const dateValue = newValue ?? newDateWithoutTime()

      if (toDate && isAfter(dateValue, toDate)) {
        setValue('toDate', dateValue)
        setValue('absencePeriod', '')

        return
      }
    },
    [setValue, toDate],
  )
  const handleAcceptToDate = useCallback(
    (newValue: Date | null) => {
      const dateValue = newValue ?? newDateWithoutTime()

      if (!fromDate) return

      if (isSameDay(dateValue, fromDate)) {
        setValue('dateRange', dateRangeOptionsKeys.singleDay)
        setValue('absencePeriod', '')

        return
      }

      setValue('dateRange', dateRangeOptionsKeys.multipleDays)
      setValue('absencePeriod', 'fullDay')
    },
    [fromDate, setValue],
  )
  const onChange = useCallback(
    ({ newValue, keyValue }: OnChangeProps) => {
      const dateValue = newValue ?? newDateWithoutTime()

      setValue(keyValue, dateValue)
    },
    [setValue],
  )
  const numDays = useMemo(() => {
    if (!toDate || !fromDate) {
      return ZERO
    }

    return daysIncludedInRange(fromDate as Date, toDate as Date)
  }, [fromDate, toDate])
  const displayNumDays = numDays > NUM_DAYS_THRESHOLD
  const infoDialogText = t('absenceRequest.new.blockedPeriod', {
    tenantName: selectedPersonTenant?.tenantName,
    blockedDays: selectedPersonTenant?.tenantAbsencesBlockingPeriod,
    firstDate: firstAvailableDate
      ? getFormattedFromDate(firstAvailableDate as Date, absenceRequestDateFormat, locale)
      : '',
  })
  const isMultipleDays = dateRange === dateRangeOptionsKeys.multipleDays
  const switchProps = {
    checked: isMultipleDays,
    dataTestId: 'date-range-selection',
    onChange: () => {
      setValue('dateRange', isMultipleDays ? dateRangeOptionsKeys.singleDay : dateRangeOptionsKeys.multipleDays)
      setValue('toDate', null)
      setValue('absencePeriod', '')
    },
    label: t('absenceRequest.new.multipleDays'),
  }

  return {
    today,
    onChange,
    handleAcceptToDate,
    handleAcceptFromDate,
    t,
    fromDate,
    toDate,
    numDays,
    displayNumDays,
    iconColor: palette.text.secondary,
    infoDialogText,
    isSmallScreen,
    fromDateError: Boolean(fromDateError),
    toDateError: Boolean(toDateError),
    maxDate: addYears(today, DAY_PICKER_MAX_YEARS),
    switchProps,
    isMultipleDays,
  }
}
